/**
 * LoginLoader Component
 * Strict 1-to-1 Live State Mapping.
 * Pre-fetches updated page sections in the background before the user clicks "Continue to Store".
 */
if (!customElements.get('login-loader')) {
  customElements.define('login-loader', class LoginLoader extends HTMLElement {
    constructor() {
      super();
      this.customerRole = 'undefined';
      this.POLL_INTERVAL_MS = 500;
      this.STEP_TRANSITION_MS = 500;
      this.STEPS = ['brand', 'discount', 'pricing', 'assign_customer'];
      this.eppDashboardRoles = ['epp', 'epp-whitelisted'];
      this.parser = new DOMParser();

      // Keywords used to skip section re-rendering
      this.SKIP_SECTION_KEYWORDS = ['header', 'footer', 'login-loader'];
      
      // Store pre-fetched HTML strings in memory
      this.prefetchedSections = new Map();
      this.isPrefetching = false;
    }

    connectedCallback() {
      this.popup = this.querySelector('#login-loader-popup');
      this.actionContainer = this.querySelector('#loader-actions');
      this.messageEl = this.querySelector('#loader-message');
      
      if (!this.popup) return;

      this.sectionId = this.dataset.sectionId;
      this.returnTo = new URLSearchParams(window.location.search).get('return_to') || window.location.pathname;
      
      this.serverStatus = {};  
      this.isProcessingUI = false;

      // Delay execution by 500ms to let login-form.js settle 
      setTimeout(() => {
        this.extractServerStatus(document);
        this.advanceUIEngine();
        this.startNetworkPoll();
      }, 500); 
    }

    
    extractServerStatus(/** @type any */ context) {
      const el = context.getElementById(`customer-status-${this.sectionId}`);
      if (el) {
        try { 
          this.serverStatus = JSON.parse(el.textContent);
          this.customerRole = this.serverStatus?.['customer_role'] || 'undefined';
          //console.log('[Login Loader] Synchronized Server Status Payload:', this.serverStatus);
        } catch (e) { 
          console.error("[Login Loader] Data parse failure:", e); 
        }
      }
    }

    /**
     * Checks if a section ID represents a true header or footer section,
     * avoiding accidental partial substring matches like 'whitelisted'.
     * @param {string} sectionId
     */
    shouldSkipSection(sectionId) {
      const lowerCaseId = sectionId.toLowerCase();

      // Strict word-boundary regex checks
      const isHeader = /(^|[-_])header([-_]|$)/i.test(lowerCaseId);
      const isFooter = /(^|[-_])footer([-_]|$)/i.test(lowerCaseId);
      const isLoader = lowerCaseId.includes('login-loader') || lowerCaseId.includes('login_loader');

      if (isHeader || isFooter || isLoader) {
        //console.log(`[Login Loader] Skipped structural section: ${sectionId}`);
        return true;
      }

      return false;
    }

    /**
     * Pre-fetches all allowed Shopify sections in the background
     * without blocking the UI or delaying the user's click action.
     */
    async prefetchAllowedSections() {
      if (this.isPrefetching) return;
      this.isPrefetching = true;

      const sectionElements = Array.from(document.querySelectorAll('[id^="shopify-section-"]'));
      if (!sectionElements.length) return;

      const targetSectionIds = sectionElements
        .map(el => el.id.replace(/^shopify-section-/, '').trim())
        .filter((id, index, self) => {
          if (!id || self.indexOf(id) !== index) return false;
          if (this.shouldSkipSection(id)) {
            //console.log(`[Login Loader] Skipping section prefetch: ${id}`);
            return false;
          }
          return true;
        });

      if (!targetSectionIds.length) return;

      //console.log('[Login Loader] Pre-fetching updated sections in background...', targetSectionIds);

      //Fetch html template and get the required section content
      try {
        const fetchUrl = `${window.location.pathname}`;
        const response = await fetch(fetchUrl);
        if (!response.ok) return;

        const html = await response.text();
        if (html.trim()) {
          targetSectionIds.map(async (secId) => {
            const doc = this.parser.parseFromString(html, 'text/html');
            const sectionHtml = doc.getElementById(`shopify-section-${secId}`)?.outerHTML;
            this.prefetchedSections.set(secId, sectionHtml);
          });
        }
      } catch (err) {
        console.warn(`[Login Loader] Failed to pre-fetch html content:`, err);
      }

      this.isPrefetching = false;
      //console.log('[Login Loader] Section pre-fetching complete.');
    }

    /**
     * Instantly replaces page sections in the DOM using pre-fetched cache.
     * If pre-fetching hasn't finished yet, falls back to live fetch.
     */
    async applySectionUpdates() {
      // If prefetch hasn't run or is incomplete, force fetch missing ones
      if (this.prefetchedSections.size === 0) {
        await this.prefetchAllowedSections();
      }

      //console.log(this.prefetchedSections);

      this.prefetchedSections.forEach((rawHtml, secId) => {
        const currentTarget = document.getElementById(`shopify-section-${secId}`);
        if (!currentTarget) return;

        const parsedDoc = this.parser.parseFromString(rawHtml, 'text/html');
        const freshTarget = parsedDoc.getElementById(`shopify-section-${secId}`);

        if (freshTarget) {
          currentTarget.replaceWith(freshTarget);
        } else {
          currentTarget.innerHTML = rawHtml;
        }

        //console.log(`[Login Loader] Applied update to section: ${secId}`);
      });

      // Notify theme JavaScript to re-initialize components / event handlers
      document.dispatchEvent(new CustomEvent('shopify:section:load', {
        bubbles: true,
        detail: { sectionId: this.sectionId }
      }));
    }

    startNetworkPoll() {
      this.pollInterval = setInterval(async () => {
        if (this.isCompleted()) {
          //this.renderCompleteState();
          clearInterval(this.pollInterval);
          return;
        }

        try {
          const fetchUrl = `${window.location.origin}${window.location.pathname}?section_id=${this.sectionId}`;          
          const response = await fetch(fetchUrl);
          if (!response.ok) throw new Error(`HTTP status fault: ${response.status}`);          
          
          const rawHtml = await response.text();
          const parsedDOM = new DOMParser().parseFromString(rawHtml, 'text/html');
          
          this.extractServerStatus(parsedDOM);          
          
          if (!this.isProcessingUI) {
            this.advanceUIEngine();
          }
        } catch (err) {
          console.warn('[Login Loader] Network polling dropped cycle, retrying...', err);
        }
      }, this.POLL_INTERVAL_MS);
    }

    advanceUIEngine() {
      this.isProcessingUI = true;

      this.STEPS.forEach((stepKey) => {
        const rowItem = this.querySelector(`[data-step="${stepKey}"]`);
        if (!rowItem) return;

        if (this.serverStatus[stepKey] === true) {
          rowItem.classList.remove('active');
          rowItem.classList.add('done');
        } else {
          rowItem.classList.remove('done');
          rowItem.classList.add('active');
        }
      });

      this.isProcessingUI = false;

      if (this.isCompleted()) {
        this.renderCompleteState();
      }
    }

    isCompleted() {
      return this.customerRole !== 'undefined' && this.STEPS.every((step) => this.serverStatus[step] === true);
    }

    renderCompleteState() {
      if (this.pollInterval) clearInterval(this.pollInterval);

      if (this.messageEl) {
        this.messageEl.textContent = "All properties verified. Your wholesale portal setup parameters are synchronized.";
      }

      // Trigger pre-fetching immediately as soon as status checks hit 100%
      this.prefetchAllowedSections().then(() => {
        this.injectActivationTrigger();
      });
    }

    injectActivationTrigger() {
      if (this.serverStatus?.['customer_role'] && !this.eppDashboardRoles.includes(this.serverStatus['customer_role'])) {
        window.location.replace(location.origin);
      }
      if (!this.actionContainer || this.actionContainer.querySelector('.loader-btn')) return;

      const actionBtn = document.createElement('button');
      actionBtn.type = 'button';
      actionBtn.className = 'loader-btn';
      actionBtn.textContent = 'Continue to Store';
      
      actionBtn.style.opacity = '0';
      actionBtn.style.transition = `opacity ${this.STEP_TRANSITION_MS}ms cubic-bezier(0.4, 0, 0.2, 1)`;
      
      actionBtn.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();

        actionBtn.disabled = true;
        actionBtn.textContent = 'Applying changes...';

        // Swap in pre-fetched updated section markup
        await this.applySectionUpdates();

        // Smoothly hide popup
        this.popup?.classList.add('hide');
      });

      this.actionContainer.appendChild(actionBtn);
      
      requestAnimationFrame(() => {
        actionBtn.style.opacity = '1';
      });
    }

    disconnectedCallback() {
      if (this.pollInterval) clearInterval(this.pollInterval);
    }
  });
}